<template>
  <div v-bind="$attrs" ref="reference" :class="fieldClasses">
    <div :class="inputContainerClasses">
      <mep-type is="label" v-if="label" :for="inputId" variant="body-large" :scheme="labelScheme" :class="labelClasses">
        {{ label }}
      </mep-type>

      <slot />

      <slot name="suffix" />
    </div>

    <mep-type v-if="!isValid" class="px-4 pt-1" scheme="error" variant="body-small">
      {{ errorMessage }}
    </mep-type>
  </div>

  <slot name="dropdown" />
</template>

<script setup lang="ts">
import { computed, provide, useTemplateRef } from 'vue';
import { cva } from 'class-variance-authority';
import { MepTypeProps } from '../../MepType/types';
import { tw } from '@/common';
import { MepInputContainerReference } from './symbols';

interface Props {
  label?: string;
  error?: string | string[] | null;
  disabled?: boolean;
  hasContent?: boolean;
  isFocused?: boolean;
  variant?: 'default' | 'multiline';
  inputId: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  error: undefined,
  disabled: false,
  hasContent: false,
  isFocused: false,
  variant: 'default',
});

const reference = useTemplateRef('reference');

// CVA variants
const fieldVariants = cva(tw`field relative`, {
  variants: {
    disabled: {
      true: tw`cursor-not-allowed opacity-80`,
    },
  },
});

const inputContainerVariants = cva(tw`input-container relative rounded bg-surface-container-lowest p-1 pl-4`, {
  variants: {
    variant: {
      default: tw`h-12`,
      multiline: tw`pt-5`,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const labelVariants = cva(tw`label pointer-events-none absolute left-4 top-1/2 -translate-y-1/2`, {
  variants: {
    variant: {
      default: '',
      multiline: tw`top-6`,
    },
    floating: {
      true: tw`floating`,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Computed properties
const isValid = computed(() => !props.error);

const errorMessage = computed(() => {
  if (Array.isArray(props.error)) {
    return props.error[0] || null;
  }
  return props.error || null;
});

const fieldClasses = computed(() => [
  fieldVariants({
    disabled: props.disabled,
  }),
  {
    focused: props.isFocused,
    invalid: !isValid.value,
  },
]);

const labelClasses = computed(() =>
  labelVariants({
    variant: props.variant,
    floating: props.hasContent || props.isFocused,
  })
);

const inputContainerClasses = computed(() =>
  inputContainerVariants({
    variant: props.variant,
  })
);

const labelScheme = computed<MepTypeProps['scheme']>(() => {
  if (!isValid.value) return 'error';
  return 'onSurfaceVariant';
});

provide(MepInputContainerReference, reference);
</script>

<style scoped>
/* Custom transitions and transforms that Tailwind can't handle */
.input-container {
  transition: outline 0.15s ease-out;
}

.label {
  transition:
    transform 0.15s ease-out,
    font-size 0.15s ease-out,
    top 0.15s ease-out;
  transform-origin: left center;
}

/* Focus and error outlines using CSS variables */
.field.focused .input-container {
  outline: 2px solid var(--theme-primary);
}

.field.invalid .input-container {
  outline: 2px solid var(--theme-error) !important;
}

/* Complex transform that requires custom CSS */
.label.floating {
  transform: translateY(-106%) scale(0.75);
  left: 16px;
}
</style>
